import { useMemo } from 'react'
import PageContainer from '@/common/components/PageContainer'
import withUserPermission from '@/common/components/WithUserPermission'
import { PermissionType } from '@/common/types'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import PlayerGameStatsListing from '../../components/PlayerGameStats/components/PlayerGameStatsListing'
import useQueryGetAllPlayersGameStats from '../../api/hooks/playlogs/useQueryGetAllPlayersGameStats'
import useQueryGetGameProviders from '../../api/hooks/playlogs/useQueryGetGameProviders'
import { useTablePagination } from '@/common/hooks/useTablePaginate'
import { useDateFilter } from '@/common/hooks/useDateFilter'
import { parseAsString, useQueryState } from 'nuqs'
import type { GetAllPlayersGameStatsParams } from '@/modules/dashboard/type'
import {
	PathsApiAdminAnalyticsAllPlayersGameStatsGetParametersQuerySortBy,
	PathsApiAdminAnalyticsAllPlayersGameStatsGetParametersQuerySortOrder,
} from '@/schema'

function PlayerGameStatsPage() {
	const { currentPage, pageSize } = useTablePagination()
	const { dateFilter } = useDateFilter()
	const [provider] = useQueryState(
		'provider',
		parseAsString.withOptions({ shallow: false }).withDefault(''),
	)
	const [sortBy] = useQueryState(
		'sortBy',
		parseAsString.withOptions({ shallow: false }).withDefault('totalPlayCount'),
	)
	const [sortOrder] = useQueryState(
		'sortOrder',
		parseAsString.withOptions({ shallow: false }).withDefault('desc'),
	)

	const params: GetAllPlayersGameStatsParams = {
		take: pageSize,
		skip: (currentPage - 1) * pageSize,
		provider: provider || undefined,
		startDate: dateFilter.startDate || undefined,
		endDate: dateFilter.endDate || undefined,
		sortBy:
			sortBy as PathsApiAdminAnalyticsAllPlayersGameStatsGetParametersQuerySortBy,
		sortOrder:
			sortOrder as PathsApiAdminAnalyticsAllPlayersGameStatsGetParametersQuerySortOrder,
	}

	const { data: playerGameStatsData, isLoading: isPlayerGameStatsLoading } =
		useQueryGetAllPlayersGameStats(params)

	const { data: providersData, isLoading: isProvidersLoading } =
		useQueryGetGameProviders({
			take: 100,
			skip: 0,
		})

	const providerOptions = useMemo(() => {
		if (!providersData?.data) return []
		return providersData.data.map((provider) => ({
			value: provider.name,
			label: provider.name,
		}))
	}, [providersData])

	const sortByOptions = [
		{ value: 'totalPlayCount', label: 'จำนวนเล่น' },
		{ value: 'totalProfit', label: 'กำไร/ขาดทุน' },
		{ value: 'totalTurnover', label: 'Turnover' },
		{ value: 'lastPlayAt', label: 'เล่นล่าสุด' },
	]

	return (
		<PageContainer>
			<PlayerGameStatsListing
				data={playerGameStatsData?.data || []}
				total={playerGameStatsData?.total || 0}
				isLoading={isPlayerGameStatsLoading || isProvidersLoading}
				providerOptions={providerOptions}
				sortByOptions={sortByOptions}
			/>
		</PageContainer>
	)
}

const GuardedPlayerGameStatsPage = withUserPermission(PlayerGameStatsPage, {
	permissionType: PermissionType.Read,
	permissionKey: [PermissionKeys.MANAGE_MARKETING],
})

export default GuardedPlayerGameStatsPage
